import axios from 'axios'
import { debounce, createDebouncedSearch } from '@/utils/debounce'

// Create axios instance.
// В production на своём домене — всегда тот же origin. На localhost (dev) — VITE_API_URL или localhost:8000.
function getBaseURL() {
  const explicit = import.meta.env.VITE_API_URL && String(import.meta.env.VITE_API_URL).trim()
  if (explicit) return explicit
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '' // тот же origin (wtc.sncode.ru и т.д.)
  }
  return import.meta.env.DEV ? 'http://localhost:8000' : ''
}

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add JWT token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add locale header
    const locale = localStorage.getItem('locale') || 'ru'
    config.headers['Accept-Language'] = locale
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const refreshToken = localStorage.getItem('refreshToken')
      
      if (refreshToken) {
        try {
          // Try to refresh token
          const response = await axios.post(
            `${api.defaults.baseURL}/api/v1/auth/refresh`,
            { refresh_token: refreshToken }
          )
          
          const { access_token, refresh_token: newRefreshToken } = response.data.data
          
          localStorage.setItem('token', access_token)
          localStorage.setItem('refreshToken', newRefreshToken)
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          
          // Redirect to login
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      } else {
        // No refresh token, clear auth
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    }
    
    return Promise.reject(error)
  }
)

// Export utility functions for components
export const setUserForLogging = (user) => {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user))
  } else {
    localStorage.removeItem('user')
  }
}

// ==========================================
// Debounced API functions for search/filters
// ==========================================

/**
 * Debounced поиск рецептов
 * Использование: searchRecipesDebounced('пицца', { filters })
 */
export const searchRecipesDebounced = createDebouncedSearch(
  async (query, options = {}, filters = {}) => {
    const params = { search: query, ...filters }
    const response = await api.get('/api/v1/recipes', { params, ...options })
    return response.data
  },
  300 // 300ms задержка
)

/**
 * Debounced поиск ингредиентов
 */
export const searchIngredientsDebounced = createDebouncedSearch(
  async (query, options = {}) => {
    const params = { search: query }
    const response = await api.get('/api/v1/ingredients', { params, ...options })
    return response.data
  },
  250 // 250ms задержка (быстрее для автокомплита)
)

/**
 * Debounced поиск категорий
 */
export const searchCategoriesDebounced = createDebouncedSearch(
  async (query, options = {}, type = null) => {
    const params = { search: query }
    if (type) params.type = type
    const response = await api.get('/api/v1/categories', { params, ...options })
    return response.data
  },
  250
)

// Экспорт утилит debounce для использования в компонентах
export { debounce, createDebouncedSearch } from '@/utils/debounce'

export default api
